import {
  FC,
  Fragment,
  PropsWithChildren,
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Transition } from '@headlessui/react';
import { cva } from 'class-variance-authority';
import { POSITION, SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';
import { MdClose } from 'react-icons/md';

import { TOUR_SELECTOR } from '../Tour';

const modalClasses = cva(
  ['fixed m-4', 'rounded-[6px] bg-fill-tertiary shadow'],
  {
    variants: {
      size: {
        [SIZE.Small]: ['w-[320px]'],
        [SIZE.Medium]: ['w-[440px]'],
        [SIZE.Large]: ['w-[600px]'],
        paper: ['w-[782px]'],
        xl: ['w-[900px]'],
      },
      position: {
        [POSITION.Center]: [],
        [POSITION.Top]: ['top-0'],
        [POSITION.TopRight]: ['top-0 right-0'],
        [POSITION.TopLeft]: ['top-0 left-0'],
        [POSITION.Bottom]: ['bottom-0'],
        [POSITION.BottomRight]: ['bottom-0 right-0'],
        [POSITION.BottomLeft]: ['bottom-0 left-0'],
        [POSITION.Left]: ['left-0'],
        [POSITION.Right]: ['right-0'],
      },
    },
    defaultVariants: { size: SIZE.Medium, position: POSITION.Center },
  },
);

type ModalSize = SIZE | 'xl' | 'paper';

interface ShowModalArgs {
  content: ReactNode;
  title?: string;
  disableOutsideClick?: boolean;
  onClickOutside?: () => void;
  hideHeader?: boolean;
  size?: ModalSize;
  position?: POSITION;
  closeCallback?: () => void;
  resetPadding?: boolean;
}

interface ModalInterface {
  showModal: (args: ShowModalArgs) => void;
  hideModal: () => void;
  open: boolean;
}

interface ModalWrapperProps {
  content?: ReactNode;
  title?: string;
  isOpen: boolean;
  closeModal: () => void;
  disableOutsideClick?: boolean;
  onClickOutside?: () => void;
  hideHeader?: boolean;
  size?: ModalSize;
  position?: POSITION;
  resetPadding?: boolean;
  className?: string;
}

export const ModalContext = createContext<ModalInterface>({
  hideModal: () => {},
  showModal: () => {},
  open: false,
});

export const ModalWrapper: FC<ModalWrapperProps> = ({
  content,
  title,
  isOpen,
  closeModal,
  disableOutsideClick,
  onClickOutside,
  hideHeader,
  size,
  position,
  resetPadding,
  className,
}) => {
  const handleCloseModal = () => {
    if (disableOutsideClick) {
      return;
    }

    closeModal();
    onClickOutside?.();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <dialog
        open={isOpen}
        className={cn('relative z-50', className)}
        onClose={handleCloseModal}
      >
        <div className="fixed inset-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div
              className={cn(
                'min-h-full',
                'overflow-y-auto',
                'flex items-center justify-center',
                'bg-[#E9EDF899] backdrop-blur-sm',
              )}
            >
              <div className={cn(modalClasses({ size, position }))}>
                {!hideHeader && (
                  <div
                    data-tut="Modal__Header"
                    className={cn(
                      'flex items-center justify-between',
                      'bg-fill-primary px-4 py-2.5',
                      'rounded-t-[6px]',
                    )}
                  >
                    <div className="text-sm font-semibold leading-5 tracking-[0.06em]">
                      {title}
                    </div>
                    <div className="font-semibold grid place-items-center">
                      <button
                        onClick={closeModal}
                        type="button"
                        className="ml-5"
                      >
                        <MdClose
                          data-tut={TOUR_SELECTOR.TOUR.MODAL_CLOSE_BUTTON}
                          className="h-4 w-4 text-secondary-dark hover:!text-danger-base transition"
                        />
                      </button>
                    </div>
                  </div>
                )}
                <div
                  className={cn(
                    !resetPadding && 'py-[22px] px-[28px]',
                    'flex gap-4 flex-col divide-y divide-separator-primary',
                  )}
                >
                  {content}
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </dialog>
    </Transition>
  );
};

const Modal: FC<PropsWithChildren> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalSize, setModalSize] = useState<ModalSize>(SIZE.Large);
  const [modalPosition, setModalPosition] = useState<POSITION>(POSITION.Center);
  const [modalContent, setModalContent] = useState<ReactNode | null>(null);
  const [hideModalHeader, setHideModalHeader] = useState(false);
  const [modalDisableOutsideClick, setDisableOutsideClick] = useState(false);
  const [closeCallback, setCloseCallback] = useState(() => () => {});
  const [resetPadding, setResetPadding] = useState<boolean | undefined>(false);

  const reset = () => {
    setHideModalHeader(false);
    setDisableOutsideClick(false);
    setModalTitle('');
    setModalSize(SIZE.Large);
    setModalPosition(POSITION.Center);
    setModalContent(null);
    setCloseCallback(() => () => {});
  };

  const showModal = useCallback(
    ({
      content,
      title = '',
      disableOutsideClick = false,
      hideHeader = false,
      size = SIZE.Large,
      position = POSITION.Center,
      resetPadding,
      closeCallback = () => {},
    }: ShowModalArgs) => {
      setModalPosition(position);
      setModalTitle(title);
      setModalSize(size);
      setDisableOutsideClick(disableOutsideClick);
      setHideModalHeader(hideHeader);
      setModalContent(content);
      setCloseCallback(() => closeCallback);
      setResetPadding(resetPadding);
    },
    [],
  );

  const hideModal = useCallback(() => {
    setOpen(false);
    closeCallback();

    // reset after transition duration
    setTimeout(reset, 201);
  }, [closeCallback]);

  useEffect(() => {
    setOpen(!!modalContent);
  }, [modalContent]);

  const value = useMemo(
    () => ({ showModal, hideModal, open }),
    [showModal, hideModal, open],
  );

  return (
    <ModalContext.Provider value={value}>
      <ModalWrapper
        disableOutsideClick={modalDisableOutsideClick}
        isOpen={open}
        closeModal={hideModal}
        content={modalContent}
        hideHeader={hideModalHeader}
        title={modalTitle}
        size={modalSize}
        position={modalPosition}
        resetPadding={resetPadding}
      />
      {children}
    </ModalContext.Provider>
  );
};

export default Modal;
